/**
 * API Service for communicating with the flashcard backend
 */
import { getLearnerId } from '../lib/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function fetchWithTimeout(url, options = {}, timeoutMs = 10000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === 'AbortError') throw new Error('Request timed out. Is the backend running?');
      throw err;
    });
}

class APIService {
  /**
   * Upload study content JSON to the backend
   */
  static async uploadContent(content) {
    try {
      const response = await fetch(`${API_BASE_URL}/upload-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading content:', error);
      throw error;
    }
  }

  /**
   * Get all available topics
   */
  static async getSubjects() {
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/subjects`);

      if (!response.ok) {
        throw new Error(`Failed to fetch subjects: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching subjects:', error);
      throw error;
    }
  }

  static async loadSubject(subject) {
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/load-subject/${encodeURIComponent(subject)}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to load subject: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading subject:', error);
      throw error;
    }
  }

  static async getTopics() {
    try {
      const response = await fetch(`${API_BASE_URL}/topics`);

      if (!response.ok) {
        throw new Error(`Failed to fetch topics: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching topics:', error);
      throw error;
    }
  }

  /**
   * Get concepts for a specific topic
   */
  static async getConcepts(topic) {
    try {
      const response = await fetch(`${API_BASE_URL}/topics/${encodeURIComponent(topic)}/concepts`);

      if (!response.ok) {
        throw new Error(`Failed to fetch concepts: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching concepts:', error);
      throw error;
    }
  }

  /**
   * Start a new learning session
   */
  static async startSession(topics, concepts = null, randomize = true, options = {}) {
    // options: { question_count, no_evaluation }
    try {
      const payload = {
        topics,
        concepts,
        randomize,
        ...options,
      };
      const response = await fetch(`${API_BASE_URL}/start-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to start session: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error starting session:', error);
      throw error;
    }
  }

  /**
   * Get flashcards (flat Q/A cards) from the backend subject content.
   * Optional subject / topic substring filters.
   */
  static async getFlashcards(subject = null, topic = null) {
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (topic) params.set('topic', topic);
    const qs = params.toString();
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/flashcards${qs ? `?${qs}` : ''}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch flashcards: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching flashcards:', error);
      throw error;
    }
  }

  /**
   * DSA — paginated problem list. Returns { total, offset, limit, items }.
   */
  static async listDsaQuestions({ difficulty, tag, search, offset = 0, limit = 25 } = {}) {
    const params = new URLSearchParams();
    if (difficulty) params.set('difficulty', difficulty);
    if (tag) params.set('tag', tag);
    if (search) params.set('search', search);
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    const response = await fetchWithTimeout(`${API_BASE_URL}/dsa/questions?${params.toString()}`);
    if (!response.ok) throw new Error(`Failed to list DSA questions: ${response.statusText}`);
    return response.json();
  }

  /**
   * DSA — fetch one problem by question_id (public view, no hidden tests).
   */
  static async getDsaQuestion(quesId) {
    const response = await fetchWithTimeout(`${API_BASE_URL}/dsa/questions/${quesId}`);
    if (!response.ok) throw new Error(`Failed to load question ${quesId}: ${response.statusText}`);
    return response.json();
  }

  /**
   * DSA — topic/pattern cards (counts + dominant difficulty).
   */
  static async getDsaTopics() {
    const response = await fetchWithTimeout(`${API_BASE_URL}/dsa/topics`);
    if (!response.ok) throw new Error(`Failed to load topics: ${response.statusText}`);
    return response.json();
  }

  /**
   * DSA — next / prev / similar problems for a question page.
   */
  static async getDsaRelated(quesId) {
    const response = await fetchWithTimeout(`${API_BASE_URL}/dsa/questions/${quesId}/related`);
    if (!response.ok) throw new Error(`Failed to load related for ${quesId}: ${response.statusText}`);
    return response.json();
  }

  static async _postDsa(path, questionId, language, sourceCode) {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/dsa/${path}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: questionId, language, source_code: sourceCode }),
      },
      30000,
    );
    if (!response.ok) {
      let detail = response.statusText;
      try { detail = (await response.json()).detail || detail; } catch (_) { /* ignore */ }
      throw new Error(detail);
    }
    return response.json();
  }

  /** DSA — run code against the sample tests (per-case results + time). */
  static async runDsa(questionId, language, sourceCode) {
    return APIService._postDsa('run', questionId, language, sourceCode);
  }

  /** DSA — submit code to be judged against ALL hidden tests. */
  static async submitDsa(questionId, language, sourceCode) {
    return APIService._postDsa('submit', questionId, language, sourceCode);
  }

  /**
   * Get the current question for a session
   */
  static async getCurrentQuestion(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/session/${sessionId}/current-question`);

      if (!response.ok) {
        throw new Error(`Failed to fetch question: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching question:', error);
      throw error;
    }
  }

  /**
   * Submit an answer for evaluation
   */
  static async submitAnswer(sessionIdOrPayload, questionId, answer, answerType = 'text', confidence = null, options = {}) {
    // Support both object and parameter-based calling
    let payload;
    if (typeof sessionIdOrPayload === 'object') {
      // Called with object
      payload = sessionIdOrPayload;
    } else {
      // Called with individual parameters
      payload = {
        session_id: sessionIdOrPayload,
        question_id: questionId,
        answer,
        answer_type: answerType,
        confidence,
        ...options,
      };
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/submit-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit answer: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting answer:', error);
      throw error;
    }
  }

  /**
   * Transcribe audio to text
   */
  static async transcribeAudio(audioBase64) {
    try {
      const response = await fetch(`${API_BASE_URL}/transcribe-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          audio_data: audioBase64,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to transcribe audio: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  }

  /**
   * Get ephemeral OpenAI Realtime API token for a session
   */
  static async getRealtimeToken(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/realtime-interview-token/${sessionId}`, {
        method: 'POST',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(err.detail || response.statusText);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting realtime token:', error);
      throw error;
    }
  }

  /**
   * Start a realtime voice interview
   */
  static async startRealtimeInterview(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/start-realtime-interview/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to start realtime interview: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error starting realtime interview:', error);
      throw error;
    }
  }

  /**
   * Delete/cancel a session
   */
  static async deleteSession(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/session/${sessionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete session: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }

  /**
   * Evaluate a completed voice interview transcript and return a structured report
   */
  static async getVoiceReport(sessionId, messages, durationSeconds = 0, hintsUsed = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/voice-interview-report/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, duration_seconds: durationSeconds, hints_used: hintsUsed }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(err.detail || response.statusText);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting voice report:', error);
      throw error;
    }
  }

  /**
   * Get session report
   */
  static async getSessionReport(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/session/${sessionId}/report`);

      if (!response.ok) {
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching report:', error);
      throw error;
    }
  }

  /**
   * Get session status
   */
  static async getSessionStatus(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/session/${sessionId}/status`);

      if (!response.ok) {
        throw new Error(`Failed to fetch status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching status:', error);
      throw error;
    }
  }

  /**
   * Health check
   */
  static async healthCheck() {
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/health`, {}, 5000);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  // ---------- agentic_engine memory ----------
  /** Consolidated memory snapshot for the signed-in learner. */
  static async getEngineMemory() {
    const lid = encodeURIComponent(getLearnerId());
    const response = await fetchWithTimeout(`${API_BASE_URL}/engine/memory?learner_id=${lid}`, {}, 15000);
    if (!response.ok) throw new Error(`Memory fetch failed: ${response.status}`);
    return response.json();
  }

  /** Upload a Markdown document → a new Knowledge Hub topic. */
  static async importMarkdown({ markdown, name }) {
    const response = await fetchWithTimeout(`${API_BASE_URL}/hub/import/markdown`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ markdown, name }),
    }, 30000);
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(detail.detail || `Import failed: ${response.status}`);
    }
    return response.json();
  }

  /** Load / save the learner's profile (settings page). */
  static async getProfile() {
    const lid = encodeURIComponent(getLearnerId());
    const response = await fetchWithTimeout(`${API_BASE_URL}/profile?learner_id=${lid}`, {}, 15000);
    if (!response.ok) throw new Error(`Profile fetch failed: ${response.status}`);
    return response.json();
  }

  /** Upload a cropped avatar — stored in Cloud Storage, doc keeps the URL. */
  static async uploadAvatar(imageDataUrl) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/profile/avatar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), image: imageDataUrl }),
    }, 30000);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || `Avatar upload failed: ${r.status}`);
    }
    return r.json();
  }

  static async removeAvatar() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/profile/avatar`, {
      method: 'DELETE', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId() }),
    }, 15000);
    if (!r.ok) throw new Error(`Avatar removal failed: ${r.status}`);
    return r.json();
  }

  static async saveProfile(profile) {
    const response = await fetchWithTimeout(`${API_BASE_URL}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...profile, learner_id: getLearnerId() }),
    }, 15000);
    if (!response.ok) throw new Error(`Profile save failed: ${response.status}`);
    return response.json();
  }

  // ---------- courses (user-authored markdown theory) ----------
  static async listCourses() {
    const lid = encodeURIComponent(getLearnerId());
    const r = await fetchWithTimeout(`${API_BASE_URL}/courses?learner_id=${lid}`, {}, 15000);
    if (!r.ok) throw new Error(`Courses fetch failed: ${r.status}`);
    return r.json();
  }

  static async getCourse(topicId) {
    const lid = encodeURIComponent(getLearnerId());
    const r = await fetchWithTimeout(`${API_BASE_URL}/courses/${encodeURIComponent(topicId)}?learner_id=${lid}`, {}, 15000);
    if (!r.ok) throw new Error(`Course fetch failed: ${r.status}`);
    return r.json();
  }

  static async saveCourse({ id, title, markdown }) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/courses`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id || '', title, markdown, learner_id: getLearnerId() }),
    }, 15000);
    if (!r.ok) throw new Error(`Course save failed: ${r.status}`);
    return r.json();
  }

  // ---------- community ----------
  static async communityProfiles() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/community/profiles`, {}, 15000);
    if (!r.ok) throw new Error(`Community fetch failed: ${r.status}`);
    return r.json();
  }

  static async communityPosts() {
    const lid = encodeURIComponent(getLearnerId());
    const r = await fetchWithTimeout(`${API_BASE_URL}/community/posts?learner_id=${lid}`, {}, 15000);
    if (!r.ok) throw new Error(`Community fetch failed: ${r.status}`);
    return r.json();
  }

  static async createCommunityPost(post) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/community/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...post, learner_id: getLearnerId() }),
    }, 15000);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || `Post failed: ${r.status}`);
    }
    return r.json();
  }

  static async toggleSubscribe(postId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/community/posts/${encodeURIComponent(postId)}/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId() }),
    }, 15000);
    if (!r.ok) throw new Error(`Subscribe failed: ${r.status}`);
    return r.json();
  }

  // ---------- creator platform (separate backend: /creator/*) ----------
  static async _creator(path, method = 'GET', body = null) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/creator${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    }, 20000);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || d.error || `Creator API failed: ${r.status}`);
    }
    return r.json();
  }

  static creatorMe() { return APIService._creator('/me'); }
  static creatorEnable() { return APIService._creator('/enable', 'POST'); }
  static creatorSaveHomepage(h) { return APIService._creator('/homepage', 'PUT', h); }
  static creatorGroups() { return APIService._creator('/groups'); }
  static creatorCreateGroup(g) { return APIService._creator('/groups', 'POST', g); }
  static creatorDeleteGroup(id) { return APIService._creator(`/groups/${id}`, 'DELETE'); }
  static creatorInterviews() { return APIService._creator('/interviews'); }
  static creatorCreateInterview(b) { return APIService._creator('/interviews', 'POST', b); }
  static creatorGetInterview(id) { return APIService._creator(`/interviews/${id}`); }
  static creatorPatchInterview(id, b) { return APIService._creator(`/interviews/${id}`, 'PUT', b); }
  static creatorDeleteInterview(id) { return APIService._creator(`/interviews/${id}`, 'DELETE'); }
  static creatorBlogs() { return APIService._creator('/blogs'); }
  static creatorGetBlog(id) { return APIService._creator(`/blogs/${id}`); }
  static creatorCreateBlog(b) { return APIService._creator('/blogs', 'POST', b); }
  static creatorUpdateBlog(id, b) { return APIService._creator(`/blogs/${id}`, 'PUT', b); }
  static creatorDeleteBlog(id) { return APIService._creator(`/blogs/${id}`, 'DELETE'); }
  static creatorPublic(id) { return APIService._creator(`/public/${encodeURIComponent(id)}`); }
  static creatorPublicBlog(id, blogId) { return APIService._creator(`/public/${encodeURIComponent(id)}/blogs/${blogId}`); }

  // ---------- admin portal ----------
  static async adminMe() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/admin/me`, {}, 12000);
    if (!r.ok) return { is_admin: false };
    return r.json();
  }

  static async adminCreators() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/admin/creators`, {}, 15000);
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || `Admin fetch failed: ${r.status}`); }
    return r.json();
  }

  static async adminSetStatus(creatorId, status) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/admin/creators/${encodeURIComponent(creatorId)}/status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    }, 15000);
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || `Status change failed: ${r.status}`); }
    return r.json();
  }

  /** Entitlements — what this user has subscribed to / can attempt. */
  static async getEntitlements() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/me/entitlements`, {}, 12000);
    if (!r.ok) return { plan: 'free', subs: {} };
    return r.json();
  }

  /* ── user_hub: YOUR hub — copy-on-write pages + AI authoring ── */
  static async listUserHubNodes() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes`, {}, 12000);
    if (!r.ok) throw new Error('Could not load your hub.');
    return r.json();
  }

  static async getUserHubNode(nodeId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes/${encodeURIComponent(nodeId)}`, {}, 12000);
    if (!r.ok) throw new Error('Could not load the page.');
    return r.json();
  }

  static async createUserHubNode(payload) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    }, 15000);
    if (!r.ok) throw new Error(((j) => j.detail || j.error)(await r.json().catch(() => ({}))) || 'Could not save the page.');
    return r.json();
  }

  static async forkHubNode(sourceNodeId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes/fork/${encodeURIComponent(sourceNodeId)}`, { method: 'POST' }, 15000);
    if (!r.ok) throw new Error('Could not create your copy.');
    return r.json();
  }

  static async updateUserHubNode(nodeId, payload) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes/${encodeURIComponent(nodeId)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    }, 15000);
    if (!r.ok) throw new Error(((j) => j.detail || j.error)(await r.json().catch(() => ({}))) || 'Could not save the page.');
    return r.json();
  }

  static async deleteUserHubNode(nodeId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes/${encodeURIComponent(nodeId)}`, { method: 'DELETE' }, 12000);
    if (!r.ok) throw new Error('Could not delete the page.');
    return r.json();
  }

  static async generateUserHubQuestions(nodeId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/nodes/${encodeURIComponent(nodeId)}/generate-questions`, { method: 'POST' }, 60000);
    if (!r.ok) throw new Error(((j) => j.detail || j.error)(await r.json().catch(() => ({}))) || 'Question generation failed.');
    return r.json();
  }

  static async composeUserHubPage(name, brief) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/compose`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, brief }),
    }, 90000);
    if (!r.ok) throw new Error(((j) => j.detail || j.error)(await r.json().catch(() => ({}))) || 'Draft failed.');
    return r.json();
  }

  /* ── user_hub ingestion: material in → reviewed draft out ──
     Long timeouts: vision + question authoring are two model passes. */
  static async _userHubIngest(path, body, timeout = 180000) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/user-hub/ingest/${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }, timeout);
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      // pydantic validation errors arrive as an array under .detail
      const msg = Array.isArray(j.detail) ? (j.detail[0]?.msg || 'Invalid input.') : (j.detail || j.error);
      throw new Error(msg || 'Ingestion failed.');
    }
    return r.json();
  }

  static ingestHubText(name, text) { return APIService._userHubIngest('text', { name, text }); }
  // 1-5 files of any mix (images/PDFs/text) → extract → organize → check → questions
  static ingestHubFiles(name, files) { return APIService._userHubIngest('files', { name, files }, 300000); }

  /** The question set for concept ids (user-hub and pack concept pages).
      NOT getHubItems — that name is the object-signature method below. */
  static async getHubQuestions(conceptIds, mode = 'theory') {
    const r = await fetchWithTimeout(`${API_BASE_URL}/hub/items?concept_ids=${encodeURIComponent(conceptIds.join(','))}&mode=${mode}&limit=30`, {}, 12000);
    if (!r.ok) throw new Error('Could not load questions.');
    return r.json();
  }

  /* ── creator packs: study material — author (Studio), subscribe, read ── */
  static creatorPacks() { return APIService._creator('/packs'); }
  static creatorCreatePack(b) { return APIService._creator('/packs', 'POST', b); }
  static creatorGetPack(id) { return APIService._creator(`/packs/${encodeURIComponent(id)}`); }
  static creatorPatchPack(id, b) { return APIService._creator(`/packs/${encodeURIComponent(id)}`, 'PATCH', b); }
  static creatorDeletePack(id) { return APIService._creator(`/packs/${encodeURIComponent(id)}`, 'DELETE'); }
  static creatorAddPackPage(id, b) { return APIService._creator(`/packs/${encodeURIComponent(id)}/pages`, 'POST', b); }
  static creatorUpdatePackPage(id, pageId, b) { return APIService._creator(`/packs/${encodeURIComponent(id)}/pages/${encodeURIComponent(pageId)}`, 'PUT', b); }
  static creatorDeletePackPage(id, pageId) { return APIService._creator(`/packs/${encodeURIComponent(id)}/pages/${encodeURIComponent(pageId)}`, 'DELETE'); }
  static creatorGeneratePackQuestions(id, pageId) { return APIService._creator(`/packs/${encodeURIComponent(id)}/pages/${encodeURIComponent(pageId)}/generate-questions`, 'POST'); }
  static creatorComposePackPage(name, brief) { return APIService._creator('/packs/compose', 'POST', { name, brief }); }

  static async _packs(path, method = 'GET') {
    const r = await fetchWithTimeout(`${API_BASE_URL}/packs${path}`, { method }, 15000);
    if (!r.ok) throw new Error(((j) => j.detail || j.error)(await r.json().catch(() => ({}))) || `Packs API failed: ${r.status}`);
    return r.json();
  }

  static discoverPacks() { return APIService._packs('/discover'); }
  static myPackSubscriptions() { return APIService._packs('/subscriptions'); }
  static subscribePack(id) { return APIService._packs(`/${encodeURIComponent(id)}/subscribe`, 'POST'); }
  static unsubscribePack(id) { return APIService._packs(`/${encodeURIComponent(id)}/subscribe`, 'DELETE'); }
  static getPack(id) { return APIService._packs(`/${encodeURIComponent(id)}`); }

  /** Index-card summaries for a set of hub nodes (one call per subtopic page). */
  static async getHubLearnSummary(ids) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/hub/learn-summary?ids=${encodeURIComponent(ids.join(','))}`, {}, 12000);
    if (!r.ok) throw new Error('Could not load the index.');
    return r.json();
  }

  /** The Learn panel's page for a hub concept (walks up to the nearest doc). */
  static async getHubLearn(nodeId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/hub/learn/${encodeURIComponent(nodeId)}`, {}, 12000);
    if (!r.ok) throw new Error('Could not load the learn page.');
    return r.json();
  }

  /** PUBLIC interview discovery — every published creator interview (card fields only). */
  static async discoverInterviews() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/creator/discover`, {}, 15000);
    if (!r.ok) throw new Error('Could not load the interview library.');
    return r.json();
  }

  static async subscribeCreator(creatorId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/subscriptions/creator/${encodeURIComponent(creatorId)}`, { method: 'POST' }, 15000);
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || `Subscribe failed: ${r.status}`); }
    return r.json();
  }

  /** Compile a creator's published interview into a live voice-room session. */
  static async startCreatorInterview(interviewId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/creator-interviews/${encodeURIComponent(interviewId)}/session`, { method: 'POST' }, 20000);
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || `Could not start: ${r.status}`); }
    return r.json();
  }

  /** PUBLIC landing social proof — approved creators with published content. */
  static async featuredCreators() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/creator/featured`, {}, 10000);
    if (!r.ok) return { creators: [] };
    return r.json();
  }

  /** Learner-facing memory tree: hub hierarchy + rolled-up understanding. */
  static async getMemoryTree() {
    const lid = encodeURIComponent(getLearnerId());
    const response = await fetchWithTimeout(`${API_BASE_URL}/engine/memory/tree?learner_id=${lid}`, {}, 20000);
    if (!response.ok) throw new Error(`Memory tree fetch failed: ${response.status}`);
    return response.json();
  }

  /** Fire-and-forget signal into the engine's memory (keyed by the learner's uid). */
  static async recordEngineEvent(evt) {
    try {
      await fetch(`${API_BASE_URL}/engine/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ learner_id: getLearnerId(), ...evt }),
      });
    } catch {
      /* non-blocking — memory writes must never break the UX */
    }
  }

  /** The agentic planner: propose (or revise, via feedback) a practice plan —
   *  rationale bullets + ranked focus areas + progress table. Fast, no content. */
  static async buildPlan({ conceptIds = [], modes = [], focus = null, difficulty = null,
                           codingDifficulty = null, weakPriority = null, countPerMode = 5,
                           feedback = null, previousPlan = null } = {}) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/session/plan`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        learner_id: getLearnerId(), concept_ids: conceptIds, modes,
        focus, difficulty, coding_difficulty: codingDifficulty,
        weak_priority: weakPriority, count_per_mode: countPerMode,
        feedback, previous_plan: previousPlan,
      }),
    }, 60000);
    if (!r.ok) throw new Error(`Plan failed: ${r.status}`);
    return r.json();
  }

  /** Turn an approved plan into ready-to-play content per mode (background). */
  static async materializeSession({ conceptIds = [], modes = [], focusAreas = [],
                                    difficulty = null, codingDifficulty = null,
                                    countPerMode = 5 } = {}) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/session/materialize`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        learner_id: getLearnerId(), concept_ids: conceptIds, modes,
        focus_areas: focusAreas, difficulty, coding_difficulty: codingDifficulty,
        count_per_mode: countPerMode,
      }),
    }, 120000);
    if (!r.ok) throw new Error(`Materialize failed: ${r.status}`);
    return r.json();
  }

  /** Pre-warm the engine's question queues for a scope (fire-and-forget).
   *  Called the moment the learner picks topics so the first question of every
   *  mode is a queue hit instead of a live LLM generation. Never throws. */
  static async engineWarm({ conceptIds = [], modes = [] } = {}) {
    try {
      await fetch(`${API_BASE_URL}/engine/warm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ learner_id: getLearnerId(), concept_ids: conceptIds, modes }),
      });
    } catch { /* warming is best-effort — never surface an error */ }
  }

  /** One MCQ generated by the engine from hub content for these hub concept ids. */
  static async engineMcq({ conceptIds = [], recent = [], difficulty = null } = {}) {
    const r = await fetch(`${API_BASE_URL}/engine/mcq`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), concept_ids: conceptIds, recent, difficulty }),
    });
    if (!r.ok) throw new Error(`MCQ generation failed: ${r.status}`);
    return r.json();
  }

  /** A Q/A flashcard deck built by the engine from hub content for these concept ids. */
  static async engineFlashcards({ conceptIds = [], count = 8 } = {}) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/flashcards`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), concept_ids: conceptIds, count }),
    }, 30000);
    if (!r.ok) throw new Error(`Flashcards failed: ${r.status}`);
    return r.json();
  }

  // ---------- theory practice ----------
  static async engineTheoryNext({ conceptIds = [], recent = [], difficulty = null } = {}) {
    const r = await fetch(`${API_BASE_URL}/engine/theory/next`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), concept_ids: conceptIds, recent, difficulty }),
    });
    if (!r.ok) throw new Error(`Theory question failed: ${r.status}`);
    return r.json();
  }

  static async engineTheoryGrade({ question, answer, conceptIds = [], itemId = null, depth = null }) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/theory/grade`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        learner_id: getLearnerId(), question, answer, concept_ids: conceptIds,
        item_id: itemId, depth,
      }),
    }, 45000);
    if (!r.ok) throw new Error(`Grading failed: ${r.status}`);
    return r.json();
  }

  /** Grade a whole typed blitz round at once — assessment happens at the END of the round. */
  static async engineBlitzGrade({ items = [] }) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/blitz/grade`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), items }),
    }, 60000);
    if (!r.ok) throw new Error(`Grading failed: ${r.status}`);
    return r.json();
  }

  static async engineTheoryDrill({ question, gap, conceptIds = [] }) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/theory/drill`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), question, gap, concept_ids: conceptIds }),
    }, 45000);
    if (!r.ok) throw new Error(`Drill failed: ${r.status}`);
    return r.json();
  }

  /** One-liner blitz questions for the Rapidfire round, hub-scoped + ranked. */
  static async engineRapidfire({ conceptIds = [], count = 12 } = {}) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/engine/rapidfire`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), concept_ids: conceptIds, count }),
    }, 60000);
    if (!r.ok) throw new Error(`Rapidfire generation failed: ${r.status}`);
    return r.json();
  }

  /** Mint the realtime session for a Rapidfire round (ephemeral key + config). */
  static async rapidfireToken({ questions, timeLimitSeconds = 25 }) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/rapidfire/token`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ questions, time_limit_seconds: timeLimitSeconds }),
    }, 30000);
    if (!r.ok) throw new Error(`Rapidfire session failed: ${r.status}`);
    return r.json();
  }

  /** Deterministic end-of-round report (built from the server's live verdicts). */
  static async rapidfireReport(sessionId, { durationSeconds = 0, messages = [] } = {}) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/rapidfire/report/${sessionId}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ duration_seconds: durationSeconds, messages }),
    }, 30000);
    if (!r.ok) throw new Error(`Rapidfire report failed: ${r.status}`);
    return r.json();
  }

  /** Ask the curator for the next DSA item (topics are LeetCode tag names). */
  static async engineNext({ topics = [], service = 'give_next_question', questionId = null, exclude = [] } = {}) {
    const r = await fetch(`${API_BASE_URL}/engine/next`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ learner_id: getLearnerId(), mode: 'dsa', service, topics, question_id: questionId, exclude }),
    });
    if (!r.ok) throw new Error(`Engine next failed: ${r.status}`);
    return r.json();
  }

  // ---------- knowledge_hub ----------
  static async getHubTree() {
    const r = await fetchWithTimeout(`${API_BASE_URL}/hub/tree`, {}, 15000);
    if (!r.ok) throw new Error(`Hub tree failed: ${r.status}`);
    return r.json();
  }

  static async getHubNode(nodeId) {
    const r = await fetchWithTimeout(`${API_BASE_URL}/hub/node/${encodeURIComponent(nodeId)}`);
    if (!r.ok) throw new Error(`Hub node failed: ${r.status}`);
    return r.json();
  }

  static async getHubItems({ conceptIds = [], mode = null, difficulty = null, limit = 20 } = {}) {
    const p = new URLSearchParams();
    if (conceptIds.length) p.set('concept_ids', conceptIds.join(','));
    if (mode) p.set('mode', mode);
    if (difficulty) p.set('difficulty', difficulty);
    p.set('limit', String(limit));
    const r = await fetchWithTimeout(`${API_BASE_URL}/hub/items?${p.toString()}`, {}, 15000);
    if (!r.ok) throw new Error(`Hub items failed: ${r.status}`);
    return r.json();
  }

  static async createHubNode({ parentId = null, name, kind = null }) {
    const r = await fetch(`${API_BASE_URL}/hub/node`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: parentId, name, kind }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Add failed: ${r.status}`);
    return r.json();
  }

  static async updateHubNode(nodeId, { name }) {
    const r = await fetch(`${API_BASE_URL}/hub/node/${encodeURIComponent(nodeId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
    });
    if (!r.ok) throw new Error(`Rename failed: ${r.status}`);
    return r.json();
  }

  static async deleteHubNode(nodeId) {
    const r = await fetch(`${API_BASE_URL}/hub/node/${encodeURIComponent(nodeId)}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`Delete failed: ${r.status}`);
    return r.json();
  }

  static async createHubItem({ nodeId, type = 'interview_q', payload = {}, difficulty = null, modes = [] }) {
    const r = await fetch(`${API_BASE_URL}/hub/item`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ node_id: nodeId, type, payload, difficulty, modes }),
    });
    if (!r.ok) throw new Error(`Add item failed: ${r.status}`);
    return r.json();
  }

  static async deleteHubItem(itemId) {
    const r = await fetch(`${API_BASE_URL}/hub/item/${encodeURIComponent(itemId)}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`Delete item failed: ${r.status}`);
    return r.json();
  }
}

export default APIService;
